import { Trash2, Play, RefreshCw, TrendingDown, AlertCircle, Shield, Flame, CheckCircle } from 'lucide-react';

const AGENT_LABELS = [
  { key: 'detection', icon: '🔍', label: 'Detection' },
  { key: 'assessment', icon: '📊', label: 'Assessment' },
  { key: 'remediation', icon: '✍️', label: 'Rewrite' },
];

function getRiskColor(level) {
  if (level === 'critical') return '#dc2626';
  if (level === 'high') return '#ef4444';
  if (level === 'medium') return '#f59e0b';
  if (level === 'low') return '#10b981';
  return 'transparent';
}

function getRiskBg(level) {
  if (level === 'critical') return 'rgba(220,38,38,0.08)';
  if (level === 'high') return 'rgba(239,68,68,0.06)';
  if (level === 'medium') return 'rgba(245,158,11,0.06)';
  if (level === 'low') return 'rgba(16,185,129,0.06)';
  return 'transparent';
}

function getRiskIcon(level) {
  if (level === 'critical') return <Flame size={10} />;
  if (level === 'high') return <AlertCircle size={10} />;
  if (level === 'medium') return <Shield size={10} />;
  return <CheckCircle size={10} />;
}

function MiniScoreRing({ score, color, size = 36 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="3" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="3"
          strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={offset}
          transform={`rotate(-90 ${size/2} ${size/2})`} style={{ transition: 'stroke-dashoffset 0.8s ease-out' }} />
      </svg>
      <div style={{
        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <span style={{ color, fontSize: 10, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>{score}</span>
      </div>
    </div>
  );
}

function StepIndicator({ status }) {
  if (status === 'done') return <span style={{ color: '#10b981', fontSize: 12 }}>✓</span>;
  if (status === 'running') return <span className="spinner" />;
  return <span style={{ color: '#3f3f46', fontSize: 12 }}>○</span>;
}

export default function TicketCard({ ticket, isAnalyzing, agentProgress, hasFailed, onClick, onAnalyze, onDelete, onDragStart, onDragEnd }) {
  const analysis = ticket.analysis;
  const assessment = analysis?.assessment;
  const detection = analysis?.detection;
  const score = assessment?.ambiguityScore;
  const riskLevel = assessment?.riskLevel;
  const hours = score != null ? Math.round(score * 0.5) : null;
  const vagueCount = detection?.vagueWordCount;
  const totalCount = detection?.totalWordCount;
  const riskColor = getRiskColor(riskLevel);

  const originalScore = ticket.originalScore;
  const hasImprovement = ticket.column === 'refined' && originalScore && score != null && originalScore !== score;

  const leftBorderColor = isAnalyzing
    ? '#6366f1'
    : analysis
      ? riskColor
      : '#2a2a3e';

  const handleDelete = (e) => { e.stopPropagation(); onDelete(); };
  const handleAnalyze = (e) => { e.stopPropagation(); onAnalyze(); };

  return (
    <div
      data-testid={`ticket-card-${ticket.id}`}
      className={`ticket-card card-enter${isAnalyzing ? ' analyzing-card' : ''}`}
      draggable={!isAnalyzing}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      style={{
        background: analysis ? `linear-gradient(135deg, #141419 0%, ${getRiskBg(riskLevel)} 100%)` : '#141419',
        border: '1px solid rgba(255,255,255,0.06)',
        borderLeft: `4px solid ${leftBorderColor}`,
        borderRadius: 12,
        padding: '14px 16px',
        marginBottom: 10,
        cursor: analysis ? 'pointer' : 'default',
        userSelect: 'none',
        position: 'relative',
        transition: 'all 0.2s ease',
        boxShadow: isAnalyzing
          ? '0 0 20px rgba(99,102,241,0.15)'
          : '0 2px 8px rgba(0,0,0,0.2)',
      }}
    >
      {/* Header row: title + delete */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: analysis ? 10 : 8 }}>
        <span style={{
          color: '#e4e4e7', fontSize: 13, fontWeight: 600, lineHeight: 1.4,
          overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', flex: 1,
        }}>
          {ticket.title}
        </span>
        <button
          data-testid={`delete-ticket-${ticket.id}`}
          onClick={handleDelete}
          style={{ background: 'none', border: 'none', color: '#3f3f46', cursor: 'pointer', padding: 2, flexShrink: 0, transition: 'color 0.15s' }}
          title="Delete ticket"
          onMouseOver={e => e.currentTarget.style.color = '#ef4444'}
          onMouseOut={e => e.currentTarget.style.color = '#3f3f46'}
        >
          <Trash2 size={13} />
        </button>
      </div>

      {/* Analyzing state */}
      {isAnalyzing && agentProgress && (
        <div style={{
          background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.1)',
          borderRadius: 8, padding: '8px 10px', marginBottom: 4,
        }}>
          {AGENT_LABELS.map(({ key, icon, label }) => {
            const status = agentProgress[key] || 'pending';
            return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <StepIndicator status={status} />
                <span style={{ fontSize: 11, color: status === 'done' ? '#10b981' : status === 'running' ? '#818cf8' : '#3f3f46', fontWeight: status === 'running' ? 600 : 400 }}>
                  {icon} {label}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Analyzed state */}
      {!isAnalyzing && analysis && assessment && (
        <div>
          {/* Score + risk row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <MiniScoreRing score={score} color={riskColor} />
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: riskColor, background: getRiskBg(riskLevel),
                  border: `1px solid ${riskColor}30`,
                }}>
                  {getRiskIcon(riskLevel)}
                  {riskLevel}
                </span>
                {hasImprovement && (
                  <span data-testid={`score-improvement-${ticket.id}`} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                    color: '#10b981', fontSize: 10, fontWeight: 700,
                    background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)',
                    borderRadius: 6, padding: '3px 6px',
                  }}>
                    <TrendingDown size={9} />
                    {originalScore} → {score}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                {hours != null && (
                  <span style={{ fontSize: 10, color: '#71717a', fontWeight: 500 }}>~{hours}h rework risk</span>
                )}
                {vagueCount != null && totalCount != null && (
                  <span style={{ fontSize: 10, color: '#52525b' }}>{vagueCount}/{totalCount} flagged</span>
                )}
              </div>
            </div>
          </div>

          {/* Clarifying question preview */}
          {ticket.column === 'needs_clarification' && analysis?.remediation?.clarifyingQuestions?.[0] && (
            <div
              data-testid={`clarifying-question-${ticket.id}`}
              style={{
                marginTop: 4, fontSize: 10, color: '#71717a', fontStyle: 'italic',
                padding: '6px 8px', background: 'rgba(245,158,11,0.04)',
                border: '1px solid rgba(245,158,11,0.08)', borderRadius: 6,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
              title={analysis.remediation.clarifyingQuestions[0]}
            >
              💡 {analysis.remediation.clarifyingQuestions[0]}
            </div>
          )}
        </div>
      )}

      {/* Unanalyzed state */}
      {!isAnalyzing && !analysis && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: hasFailed ? '#ef4444' : '#52525b', fontSize: 11, fontWeight: 500 }}>
            {hasFailed ? 'Analysis failed' : 'Awaiting analysis'}
          </span>
          <button
            data-testid={`analyze-btn-${ticket.id}`}
            onClick={handleAnalyze}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: hasFailed ? 'rgba(239,68,68,0.08)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${hasFailed ? 'rgba(239,68,68,0.2)' : 'rgba(99,102,241,0.2)'}`,
              borderRadius: 8, padding: '5px 12px',
              color: hasFailed ? '#ef4444' : '#818cf8',
              fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              transition: 'all 0.15s',
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = hasFailed ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.15)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = hasFailed ? 'rgba(239,68,68,0.08)' : 'rgba(99,102,241,0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {hasFailed ? <><RefreshCw size={10} /> Retry</> : <><Play size={10} /> Analyze</>}
          </button>
        </div>
      )}
    </div>
  );
}
